/**
 * Formatting utilities — money, time, numbers.
 * All financial values come from the API as integer cents; format here for display.
 */

/** Format cents as a currency string with direction sign. */
export function formatMoney(
  cents: number,
  options: { currency?: string; signed?: boolean } = {},
): string {
  const { currency = 'KRW', signed = false } = options
  const abs = Math.abs(cents)
  const sign = cents < 0 ? '−' : signed ? '+' : ''

  if (currency === 'KRW') {
    return `${sign}₩${(abs).toLocaleString('ko-KR')}`
  }
  if (currency === 'USD') {
    return `${sign}$${(abs / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  // Generic fallback
  return `${sign}${(abs / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })} ${currency}`
}

/** Short relative time in Korean: "방금 전", "3분 전", "2시간 전", "어제", "3일 전", or absolute date. */
export function formatRelativeTime(iso: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const now = Date.now()
  const diffMs = now - then
  const diffMin = Math.floor(diffMs / 60_000)

  if (diffMin < 1) return '방금 전'
  if (diffMin < 60) return `${diffMin}분 전`

  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}시간 전`

  const diffDay = Math.floor(diffHr / 24)
  if (diffDay === 1) return '어제'
  if (diffDay < 7) return `${diffDay}일 전`

  // 7+ days → absolute date
  return new Date(iso).toISOString().slice(0, 10)
}

/** Compact number: 1250000 → "1,250,000" (ko-KR grouping). */
export function formatNumber(n: number): string {
  return n.toLocaleString('ko-KR')
}

/** Days until an ISO date. Negative if past. */
export function daysUntil(iso: string | null): number | null {
  if (!iso) return null
  const target = new Date(iso).getTime()
  if (Number.isNaN(target)) return null
  const diffMs = target - Date.now()
  return Math.floor(diffMs / 86_400_000)
}

/** Format a month label for the current date: "2026년 6월". */
export function currentMonthLabel(now: Date = new Date()): string {
  return `${now.getUTCFullYear()}년 ${now.getUTCMonth() + 1}월`
}
